import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

declare global {
    interface Window {
        fbq: any;
        _fbq: any;
    }
}

type StandardEvent =
    | 'PageView'
    | 'ViewContent'
    | 'Search'
    | 'AddToCart'
    | 'AddToWishlist'
    | 'InitiateCheckout'
    | 'AddPaymentInfo'
    | 'Purchase'
    | 'Lead'
    | 'CompleteRegistration';

interface EventData {
    value?: number;
    currency?: string;
    content_name?: string;
    content_ids?: string[];
    content_type?: string;
    [key: string]: any;
}

interface FacebookPixelContextType {
    trackEvent: (eventName: StandardEvent, data?: EventData) => void;
    trackCustomEvent: (eventName: string, data?: Record<string, any>) => void;
    grantConsent: () => void;
    revokeConsent: () => void;
    hasConsent: boolean;
    isInitialized: boolean;
}

interface FacebookPixelProviderProps {
    children: ReactNode;
    pixelId: string;
    autoPageView?: boolean;
    requireConsent?: boolean;
    debug?: boolean;
}

const FacebookPixelContext = createContext<FacebookPixelContextType | undefined>(
    undefined
);

export const FacebookPixelProvider: React.FC<FacebookPixelProviderProps> = ({
    children,
    pixelId,
    autoPageView = true,
    requireConsent = false,
    debug = false,
}) => {
    const [hasConsent, setHasConsent] = useState<boolean>(!requireConsent);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    useEffect(() => {
        if (requireConsent && typeof window !== 'undefined') {
            const savedConsent = localStorage.getItem('fb_pixel_consent');
            if (savedConsent === 'granted') {
                setHasConsent(true);
            }
        }
    }, [requireConsent]);

    useEffect(() => {
        if (typeof window === 'undefined' || !hasConsent || isInitialized) {
            return;
        }

        if (!pixelId) {
            console.warn('[Meta Pixel] Pixel ID not configured — skipping init');
            return;
        }

        try {
            (function (f: any, b: Document, e: string, v: string, n?: any, t?: any, s?: any) {
                if (f.fbq) return;
                n = f.fbq = function () {
                    n.callMethod
                        ? n.callMethod.apply(n, arguments)
                        : n.queue.push(arguments);
                };
                if (!f._fbq) f._fbq = n;
                n.push = n;
                n.loaded = true;
                n.version = '2.0';
                n.queue = [];
                t = b.createElement(e);
                t.async = true;
                t.src = v;
                s = b.getElementsByTagName(e)[0];
                s.parentNode?.insertBefore(t, s);
            })(
                window,
                document,
                'script',
                'https://connect.facebook.net/en_US/fbevents.js'
            );

            // Initialize pixel
            window.fbq('init', pixelId);

            // Track initial PageView if enabled
            if (autoPageView) {
                window.fbq('track', 'PageView');
            }

            setIsInitialized(true);

            if (debug) {
                console.log(`[Facebook Pixel] Initialized with ID: ${pixelId}`);
            }
        } catch (error) {
            console.error('[Facebook Pixel] Initialization error:', error);
        }
    }, [pixelId, hasConsent, autoPageView, debug, isInitialized]);

    const grantConsent = (): void => {
        setHasConsent(true);
        if (typeof window !== 'undefined') {
            localStorage.setItem('fb_pixel_consent', 'granted');
        }
        if (debug) {
            console.log('[Facebook Pixel] Consent granted');
        }
    };

    const revokeConsent = (): void => {
        setHasConsent(false);
        setIsInitialized(false);
        if (typeof window !== 'undefined') {
            localStorage.setItem('fb_pixel_consent', 'revoked');
        }
        if (debug) {
            console.log('[Facebook Pixel] Consent revoked');
        }
    };

    const trackEvent = (eventName: StandardEvent, data: EventData = {}): void => {
        if (!hasConsent) {
            if (debug) {
                console.warn('[Facebook Pixel] Tracking blocked - no consent');
            }
            return;
        }

        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('track', eventName, data);
            if (debug) {
                console.log(`[Facebook Pixel] Event tracked: ${eventName}`, data);
            }
        } else {
            console.warn('[Facebook Pixel] Not initialized');
        }
    };

    const trackCustomEvent = (
        eventName: string,
        data: Record<string, any> = {}
    ): void => {
        if (!hasConsent) {
            if (debug) {
                console.warn('[Facebook Pixel] Tracking blocked - no consent');
            }
            return;
        }

        if (typeof window !== 'undefined' && window.fbq) {
            window.fbq('trackCustom', eventName, data);
            if (debug) {
                console.log(`[Facebook Pixel] Custom event tracked: ${eventName}`, data);
            }
        } else {
            console.warn('[Facebook Pixel] Not initialized');
        }
    };

    return (
        <FacebookPixelContext.Provider
            value={{
                trackEvent,
                trackCustomEvent,
                grantConsent,
                revokeConsent,
                hasConsent,
                isInitialized,
            }}
        >
            {children}
        </FacebookPixelContext.Provider>
    );
};

// No-op fallback when Facebook Pixel is unavailable
const noopPixel: FacebookPixelContextType = {
    trackEvent: () => {},
    trackCustomEvent: () => {},
    grantConsent: () => {},
    revokeConsent: () => {},
    hasConsent: false,
    isInitialized: false,
};

// Custom hook — returns no-ops if provider is unavailable
export const useFacebookPixel = (): FacebookPixelContextType => {
    const context = useContext(FacebookPixelContext);
    return context ?? noopPixel;
};

// Cookie consent banner component
export const CookieConsentBanner: React.FC = () => {
    const { hasConsent, grantConsent } = useFacebookPixel();
    const [visible, setVisible] = useState<boolean>(!hasConsent);

    if (!visible || hasConsent) {
        return null;
    }

    const handleAccept = (): void => {
        grantConsent();
        setVisible(false);
    };

    const handleDecline = (): void => {
        setVisible(false);
    };

    const bannerStyle: React.CSSProperties = {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#2c3e50',
        color: 'white',
        padding: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 9999,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
    };

    const buttonStyle: React.CSSProperties = {
        padding: '10px 20px',
        marginLeft: '10px',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
    };

    return (
        <div style={bannerStyle}>
            <p style={{ margin: 0, flex: 1 }}>
                We use cookies and tracking pixels to improve your experience and analyze site
                traffic. By clicking "Accept", you consent to our use of cookies.
            </p>
            <div>
                <button
                    onClick={handleDecline}
                    style={{
                        ...buttonStyle,
                        backgroundColor: '#95a5a6',
                        color: 'white',
                    }}
                >
                    Decline
                </button>
                <button
                    onClick={handleAccept}
                    style={{
                        ...buttonStyle,
                        backgroundColor: '#27ae60',
                        color: 'white',
                    }}
                >
                    Accept
                </button>
            </div>
        </div>
    );
};

export type { StandardEvent, EventData, FacebookPixelContextType };